import React from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/auth";
import LoginPage from "@/pages/LoginPage";
import SetPasswordPage from "@/pages/SetPasswordPage";
import ForgotPage from "@/pages/ForgotPage";
import MyTripsPage from "@/pages/MyTripsPage";
import TripLayout from "@/pages/TripLayout";
import TodayPage from "@/pages/TodayPage";
import ItineraryPage from "@/pages/ItineraryPage";
import BudgetPage from "@/pages/BudgetPage";
import ExplorePage from "@/pages/ExplorePage";
import MorePage from "@/pages/MorePage";
import DiaryPage from "@/pages/DiaryPage";
import AdminDashboard from "@/pages/AdminDashboard";
import { InviteClientPage, NewTripPage } from "@/pages/AdminForms";
import AdminTripEditor from "@/pages/AdminTripEditor";
import AdminClientDetailPage from "@/pages/AdminClientDetailPage";
import TeamPage from "@/pages/TeamPage";
import { PaymentSuccessPage, PaymentCancelPage } from "@/pages/PaymentPages";
import DocumentsPage from "@/pages/DocumentsPage";
import ConciergePage from "@/pages/ConciergePage";
import MemoryBookOrderPage from "@/pages/MemoryBookOrderPage";
import ExperiencesPage from "@/pages/ExperiencesPage";
import TemplatesPage from "@/pages/TemplatesPage";
import PrintQueuePage from "@/pages/PrintQueuePage";
import InstallPushBanner from "@/components/InstallPushBanner";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 tcm-eyebrow">Loading...</div>;
  if (!user) return <Navigate to="/" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <LoginPage />;
  if (user.role === "lead_client") return <Navigate to="/trips" replace />;
  return <Navigate to="/admin" replace />;
}

function App() {
  // Auto-forward anyone opening an auth/magic-link URL on the emergent preview
  // host to the real portal domain, preserving path + token + email query.
  // Protects clients who click a legacy invite email that embedded the old
  // preview URL. Runs once at module scope so it fires before React mounts.
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    const path = window.location.pathname;
    const CANONICAL = "https://portal.the-curated-mile.com";
    const AUTH_PATHS = ["/set-password", "/reset-password", "/forgot"];
    if (host.endsWith(".preview.emergentagent.com") &&
        AUTH_PATHS.some((p) => path === p || path.startsWith(p + "/"))) {
      window.location.replace(CANONICAL + path + window.location.search + window.location.hash);
    }
  }
  return (
    <AuthProvider>
      <Toaster position="top-center" />
      <BrowserRouter>
        <InstallPushBanner />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/set-password" element={<SetPasswordPage />} />
          <Route path="/reset-password" element={<SetPasswordPage />} />
          <Route path="/forgot" element={<ForgotPage />} />
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/payment/cancel" element={<PaymentCancelPage />} />
          <Route path="/trips" element={<Protected roles={["lead_client","admin","staff"]}><MyTripsPage /></Protected>} />
          <Route path="/trips/:tripId" element={<Protected roles={["lead_client","admin","staff"]}><TripLayout /></Protected>}>
            <Route index element={<TodayPage />} />
            <Route path="itinerary" element={<ItineraryPage />} />
            <Route path="budget" element={<BudgetPage />} />
            <Route path="explore" element={<ExplorePage />} />
            <Route path="diary" element={<DiaryPage />} />
            <Route path="documents" element={<DocumentsPage />} />
            <Route path="memory-book" element={<MemoryBookOrderPage />} />
            <Route path="experiences" element={<ExperiencesPage />} />
            <Route path="concierge" element={<ConciergePage />} />
            <Route path="more" element={<MorePage />} />
          </Route>
          <Route path="/admin" element={<Protected roles={["admin","staff"]}><AdminDashboard /></Protected>} />
          <Route path="/admin/clients/new" element={<Protected roles={["admin","staff"]}><InviteClientPage /></Protected>} />
          <Route path="/admin/trips/new" element={<Protected roles={["admin","staff"]}><NewTripPage /></Protected>} />
          <Route path="/admin/trips/:tripId" element={<Protected roles={["admin","staff"]}><AdminTripEditor /></Protected>} />
          <Route path="/admin/clients/:tripId" element={<Protected roles={["admin","staff"]}><AdminClientDetailPage /></Protected>} />
          <Route path="/admin/team" element={<Protected roles={["admin"]}><TeamPage /></Protected>} />
          <Route path="/admin/templates" element={<Protected roles={["admin","staff"]}><TemplatesPage /></Protected>} />
          <Route path="/admin/print-queue" element={<Protected roles={["admin","staff"]}><PrintQueuePage /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
